import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import ModuleLayout from '../components/ModuleLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Target, Calculator } from 'lucide-react';
import BudgetTracker from '../components/accounting/BudgetTracker';
import BreakEvenCalc from '../components/accounting/BreakEvenCalc';
import ExportToolbar from '../components/accounting/ExportToolbar';

export default function PlanningPage() {
  const { token, activeBranch } = useAuth();
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const branchId = activeBranch?.id;
  const [budgetData, setBudgetData] = useState(null);
  const [breakEvenData, setBreakEvenData] = useState(null);

  const fmt = (v) => Number(v || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const toCamelCase = (str) => str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

  const buildReport = useCallback(() => {
    const sar = t('common.sar');
    const sections = [];

    if (budgetData?.items?.length) {
      sections.push({
        type: 'table', title: t('accounting.budgetVsActual') + ' - ' + (budgetData.month || ''),
        headers: [t('accounting.category'), t('budget.totalBudget'), t('budget.totalActual'), t('budget.remaining'), '%'],
        rows: [
          ...budgetData.items.map(it => ({
            cells: [
              { value: t(`accounting.${toCamelCase(it.category)}`) },
              { value: `${fmt(it.budget)} ${sar}`, type: 'num' },
              { value: `${fmt(it.actual)} ${sar}`, type: 'num' },
              { value: `${fmt(it.remaining)} ${sar}`, type: 'num', color: it.remaining < 0 ? 'negative' : 'positive' },
              { value: `${it.pct_used}%`, type: 'num', color: it.pct_used > 100 ? 'negative' : it.pct_used > 80 ? '' : 'positive' },
            ]
          })),
          {
            _total: true,
            cells: [
              { value: t('common.total') },
              { value: `${fmt(budgetData.totals.budget)} ${sar}`, type: 'num' },
              { value: `${fmt(budgetData.totals.actual)} ${sar}`, type: 'num' },
              { value: `${fmt(budgetData.totals.remaining)} ${sar}`, type: 'num', color: budgetData.totals.remaining < 0 ? 'negative' : 'positive' },
              { value: `${budgetData.totals.pct_used}%`, type: 'num' },
            ]
          }
        ]
      });
    }

    if (breakEvenData) {
      const be = breakEvenData;
      sections.push({
        type: 'kpis', title: t('accounting.breakEven'),
        items: [
          { label: t('breakEven.monthlyTarget'), value: fmt(be.break_even_revenue), unit: sar },
          { label: t('accounting.totalRevenue'), value: fmt(be.total_revenue), unit: sar, highlight: be.reached_break_even ? 'positive' : '' },
          { label: be.reached_break_even ? t('breakEven.surplus') : t('breakEven.deficit'), value: fmt(Math.abs(be.surplus_or_deficit)), unit: sar, highlight: be.reached_break_even ? 'positive' : 'negative' },
          { label: t('breakEven.cmRatio'), value: `${be.contribution_margin_ratio}%` },
          { label: t('breakEven.fixedCosts'), value: fmt(be.total_fixed_costs), unit: sar },
          { label: t('breakEven.variableCosts'), value: fmt(be.total_variable_costs), unit: sar },
        ]
      });
    }

    return sections.length ? { kpis: [], sections } : null;
  }, [budgetData, breakEvenData, t, lang, fmt]);

  return (
    <ModuleLayout moduleKey="finance">
      <div className="space-y-5" data-testid="planning-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{t('nav.planning')}</h1>
            <p className="text-sm text-muted-foreground mt-1">{activeBranch?.name || ''}</p>
          </div>
          <ExportToolbar title={t('nav.planning')} subtitle={activeBranch?.name} buildReport={buildReport} t={t} lang={lang} />
        </div>
        <Tabs defaultValue="budget" className="w-full">
          <TabsList className="w-full grid grid-cols-2 h-auto p-1 bg-muted/50" data-testid="planning-tabs">
            <TabsTrigger value="budget" className="text-xs sm:text-sm py-2" data-testid="tab-budget">
              <Target className="w-4 h-4 me-1.5" />{t('accounting.budgetVsActual')}
            </TabsTrigger>
            <TabsTrigger value="breakeven" className="text-xs sm:text-sm py-2" data-testid="tab-breakeven">
              <Calculator className="w-4 h-4 me-1.5" />{t('accounting.breakEven')}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="budget">
            <BudgetTracker branchId={branchId} token={token} t={t} lang={lang} onDataReady={setBudgetData} />
          </TabsContent>
          <TabsContent value="breakeven">
            <BreakEvenCalc branchId={branchId} token={token} t={t} lang={lang} onDataReady={setBreakEvenData} />
          </TabsContent>
        </Tabs>
      </div>
    </ModuleLayout>
  );
}
